<template>
    <div class="max-w-(--breakpoint-lg) flex flex-col gap-8">
        <page-header class="mb-0!">Intelligent Inbox</page-header>

        <page-loading v-if="loading"></page-loading>

        <vx-alert v-if="!loading && loadError" color="danger" :closeable="false" shadow>
            <div class="flex items-center justify-between">
                Failed to load Intelligent Inbox settings
                <vx-button data-test="retry-btn" @click="fetchSettings" size="sm" color="danger">
                    Retry <font-awesome-icon widthAuto :icon="faRefresh" />
                </vx-button>
            </div>
        </vx-alert>

        <template v-if="!loading && !loadError">
            <vx-expandable-card color="info">
                <template v-slot:content>
                    <div class="flex flex-col gap-6">
                        <div>
                            <span class="font-extrabold text-2xl text-slate-700">Relevance Window</span>
                            <p class="text-sm text-slate-500 mb-0">
                                Conversations with no interaction for this many days move to Done and are no longer
                                re-evaluated.
                            </p>
                        </div>

                        <vx-input
                            v-model="form.stale_window_days"
                            type="number"
                            size="md"
                            :min="MIN_STALE_WINDOW_DAYS"
                            :max="MAX_STALE_WINDOW_DAYS"
                            :disabled="processing"
                            :error="displayFirstError(validation.stale_window_days)"
                            aria-label="Relevance Window"
                            data-test="stale-window-input"
                        />
                    </div>
                </template>
            </vx-expandable-card>

            <vx-expandable-card color="info">
                <template v-slot:content>
                    <div class="flex flex-col gap-6">
                        <div>
                            <span class="font-extrabold text-2xl text-slate-700">Categories</span>
                            <p class="text-sm text-slate-500 mb-0">
                                Configure how Intelligent Inbox classifies and prioritizes conversations.
                            </p>
                        </div>

                        <vx-alert v-if="categories.length === 0" color="primary" :closeable="false">
                            Add your first category to enable Intelligent Inbox for your groups.
                        </vx-alert>

                        <vx-table
                            v-if="categories.length"
                            ref="sortableTable"
                            class="xl:grid-cols-[auto_minmax(0,1fr)_minmax(0,2fr)_auto]"
                            rowBreakpoint="xl"
                        >
                            <vx-table-row isHeader>
                                <div></div>
                                <div>Name</div>
                                <div>Classification Instructions</div>
                            </vx-table-row>

                            <intelligent-inbox-category-row
                                v-for="(category, index) in categories"
                                :key="category.id ?? category.name + '-' + index"
                                class="category-draggable"
                                :modelValue="categories[index]!"
                                :disabled="processing"
                                @update:modelValue="(value: ThreadCategoryType) => updateCategory(index, value)"
                                @edit="openEdit(index)"
                                @delete="removeCategory(index)"
                            />
                        </vx-table>

                        <div v-if="categories.length < MAX_CATEGORIES" class="flex">
                            <vx-box class="p-4">
                                <vx-button
                                    data-test="new-category-btn"
                                    size="lg"
                                    :disabled="processing"
                                    @click="openCreate"
                                >
                                    New Category
                                    <font-awesome-icon :icon="faCirclePlus"></font-awesome-icon>
                                </vx-button>
                            </vx-box>
                        </div>

                        <vx-alert v-if="categories.length >= MAX_CATEGORIES" color="info" :closeable="false">
                            Maximum of {{ MAX_CATEGORIES }} categories reached.
                        </vx-alert>
                    </div>
                </template>
            </vx-expandable-card>

            <membership-status-definitions-settings v-model="membershipStatusDefinitions" :disabled="processing" />
            <lifecycle-status-definitions-settings v-model="lifecycleStatusDefinitions" :disabled="processing" />

            <vx-alert v-if="dirty" color="info" :closeable="false">
                You have unsaved changes. Save to apply them across all sections.
            </vx-alert>

            <vx-sticky-footer>
                <vx-button
                    class="w-20"
                    size="lg"
                    color="muted"
                    data-test="cancel-btn"
                    :disabled="!dirty || processing"
                    @click.prevent="discardChanges()"
                >
                    Cancel
                </vx-button>
                <vx-button
                    class="grow"
                    size="lg"
                    color="primary"
                    data-test="save-all-btn"
                    :loading="processing"
                    :disabled="!dirty"
                    @click.prevent="submit()"
                >
                    Update
                </vx-button>
            </vx-sticky-footer>
        </template>

        <modal-thread-category
            v-model:visible="modalVisible"
            :category="modalCategory"
            :editIndex="modalEditIndex"
            @saved="onCategorySaved"
        />
    </div>
</template>

<script lang="ts" setup>
import { faCirclePlus, faRefresh } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxAlert,
    VxBox,
    VxButton,
    VxExpandableCard,
    VxInput,
    VxStickyFooter,
    VxSwal,
    VxTable,
    VxTableRow,
} from '@voxie/frontend-components';
import { integer, maxValue, minValue, required } from '@vuelidate/validators';
import { moveArrayElement, useSortable } from '@vueuse/integrations/useSortable';
import { computed, ref } from 'vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import IntelligentInboxCategoryRow from './IntelligentInboxCategoryRow.vue';
import LifecycleStatusDefinitionsSettings from './LifecycleStatusDefinitionsSettings.vue';
import MembershipStatusDefinitionsSettings from './MembershipStatusDefinitionsSettings.vue';
import ModalThreadCategory from './ModalThreadCategory.vue';
import { DEFAULT_STALE_WINDOW_DAYS, MAX_STALE_WINDOW_DAYS, MIN_STALE_WINDOW_DAYS } from '~/constants/messageHub';
import { intelligentInboxSettingsClient } from '~/services';
import type {
    IntelligentInboxSettingsResponse,
    LifecycleStatusDefinitionType,
    MembershipStatusDefinitionType,
    StatusDefinitionFormData,
    StatusDefinitionType,
    ThreadCategoryFormData,
    ThreadCategoryType,
} from '~/services/intelligentInboxSettings/intelligentInboxSettingsClient.types';
import { useFormSubmission } from '~/composables/useFormSubmission';
import { escapeHtml } from '~/utils/html';
import { displayFirstError } from '~/utils/validation';

const MAX_CATEGORIES = 20;
const teamId = Spark.state.currentTeam.id;

const loading = ref(false);
const loadError = ref(false);
const form = ref<{ stale_window_days: number | string }>({ stale_window_days: DEFAULT_STALE_WINDOW_DAYS });
const categories = ref<ThreadCategoryType[]>([]);
const lifecycleStatusDefinitions = ref<LifecycleStatusDefinitionType[]>([]);
const membershipStatusDefinitions = ref<MembershipStatusDefinitionType[]>([]);
const original = ref('');
const savedSettings = ref<IntelligentInboxSettingsResponse | null>(null);

const modalVisible = ref(false);
const modalCategory = ref<ThreadCategoryType | null>(null);
const modalEditIndex = ref<number | null>(null);

const toCategoryFormData = (category: ThreadCategoryType): ThreadCategoryFormData => ({
    name: category.name,
    description: category.description,
    classification_instructions: category.classification_instructions,
    color: category.color,
});

const toDefinitionFormData = (definition: StatusDefinitionType): StatusDefinitionFormData => ({
    name: definition.name,
    classification_steering: definition.classification_steering,
    message_steering: definition.message_steering,
});

const formData = computed(() => ({
    stale_window_days: Number(form.value.stale_window_days),
    categories: categories.value.map(toCategoryFormData),
    lifecycle_status_definitions: lifecycleStatusDefinitions.value.map(toDefinitionFormData),
    membership_status_definitions: membershipStatusDefinitions.value.map(toDefinitionFormData),
}));

const dirty = computed(() => JSON.stringify(formData.value) !== original.value);

const applySettings = (settings: IntelligentInboxSettingsResponse): void => {
    form.value.stale_window_days = settings.stale_window_days;
    // Copies, so the sortable's in-place moves leave the saved settings intact for Cancel.
    categories.value = [...settings.categories];
    lifecycleStatusDefinitions.value = [...settings.lifecycle_status_definitions];
    membershipStatusDefinitions.value = [...settings.membership_status_definitions];
    original.value = JSON.stringify(formData.value);
    savedSettings.value = settings;
};

const discardChanges = (): void => {
    if (null !== savedSettings.value) {
        applySettings(savedSettings.value);
    }
};

const openCreate = (): void => {
    modalCategory.value = null;
    modalEditIndex.value = null;
    modalVisible.value = true;
};

const openEdit = (index: number): void => {
    modalCategory.value = categories.value[index] ?? null;
    modalEditIndex.value = index;
    modalVisible.value = true;
};

const updateCategory = (index: number, updatedCategory: ThreadCategoryType): void => {
    if (undefined === categories.value[index]) {
        return;
    }
    categories.value = categories.value.map((category, i) => (i === index ? updatedCategory : category));
};

const removeCategory = (index: number): void => {
    const category = categories.value[index];

    VxSwal.confirmation(
        'Are you sure?',
        `Are you sure you want to delete "${escapeHtml(category?.name ?? '')}"?`,
        'danger',
        async () => {
            categories.value = categories.value.filter((_, i) => i !== index);
        }
    );
};

const onCategorySaved = (data: ThreadCategoryFormData, editIndex: number | null): void => {
    if (null !== editIndex && undefined !== categories.value[editIndex]) {
        categories.value = categories.value.map((category, i) =>
            i === editIndex ? { ...category, ...data } : category
        );
    } else {
        categories.value = [...categories.value, { ...data } as ThreadCategoryType];
    }
    modalVisible.value = false;
};

const { validation, submit, processing } = useFormSubmission({
    data: form,
    validation: {
        stale_window_days: {
            required,
            integer,
            minValue: minValue(MIN_STALE_WINDOW_DAYS),
            maxValue: maxValue(MAX_STALE_WINDOW_DAYS),
        },
    },
    scope: false,
    submit: async () => {
        const response = await intelligentInboxSettingsClient.updateIntelligentInboxSettings(
            { teamId },
            formData.value
        );
        applySettings(response.data);
    },
    notification: () => ({ message: 'Intelligent Inbox configuration saved' }),
    preserveScroll: true,
});

const sortableTable = ref<InstanceType<typeof VxTable> | null>(null);

useSortable(sortableTable, categories, {
    handle: '.category-handle',
    draggable: '.category-draggable',
    animation: 200,
    watchElement: true,
    // The header row shares the table grid, so the plain indices count it and the draggable ones do not.
    onUpdate: (e: { oldDraggableIndex: number; newDraggableIndex: number }) => {
        moveArrayElement(categories.value, e.oldDraggableIndex, e.newDraggableIndex);
    },
} as Record<string, unknown>);

const fetchSettings = async (): Promise<void> => {
    loading.value = true;
    loadError.value = false;

    try {
        const response = await intelligentInboxSettingsClient.getIntelligentInboxSettings({ teamId });
        applySettings(response.data);
    } catch {
        loadError.value = true;
    } finally {
        loading.value = false;
    }
};

fetchSettings();
</script>
